import { db } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { User } from '@/domain/types/user';
import { addMembers } from '@/domain/use-cases/audiences/audience-service';
import {
  filterUsers,
  MemberSearchField,
} from '@/domain/use-cases/audiences/member-list';
import { planAddition } from '@/domain/use-cases/audiences/membership-rules';
import { userFromDoc } from '@/domain/use-cases/audiences/user-from-doc';
import Button from '@/presentation/components/button';
import Spinner from '@/presentation/components/spinner';
import {
  excludeInactiveUsersFilter,
  excludeSelfRegisteredUsersFilter,
} from '@/presentation/pages/users/visible-users-filter';
import {
  and,
  collection,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { plural, searchOptions } from './search-options';

const BROWSE_PAGE_SIZE = 200;
// Carácter alto de Unicode: cierra el rango de una búsqueda por prefijo en Firestore.
const PREFIX_END = '';

type AddMembersPanelProps = {
  audienceId: string;
  memberIds: Set<string>;
  onClose: () => void;
  onAdded: (added: User[]) => void;
};

const mergeById = (...lists: User[][]): User[] => {
  const byId = new Map<string, User>();
  lists.flat().forEach((user) => {
    if (!byId.has(user.id)) byId.set(user.id, user);
  });
  return Array.from(byId.values());
};

const AddMembersPanel = ({
  audienceId,
  memberIds,
  onClose,
  onAdded,
}: AddMembersPanelProps) => {
  const [browse, setBrowse] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [field, setField] = useState<MemberSearchField>('document');
  const [term, setTerm] = useState('');
  const [serverResults, setServerResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<Record<string, User>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getDocs(
      query(
        collection(db, DOCUMENTS_DB.USERS),
        excludeSelfRegisteredUsersFilter(),
        excludeInactiveUsersFilter(),
        orderBy('name'),
        limit(BROWSE_PAGE_SIZE),
      ),
    )
      .then((snapshot) => setBrowse(snapshot.docs.map(userFromDoc)))
      .catch(() =>
        toast.error('Error al cargar los usuarios', { theme: 'colored' }),
      )
      .finally(() => setIsLoading(false));
  }, []);

  const runSearch = useCallback(
    async (searchField: MemberSearchField, text: string) => {
      setIsSearching(true);
      try {
        const collectionRef = collection(db, DOCUMENTS_DB.USERS);
        const lowered = text.toLowerCase();
        const queryRef =
          searchField === 'document'
            ? query(
                collectionRef,
                and(
                  or(
                    where('document', '==', text),
                    where('document', '==', parseInt(text)),
                  ),
                  excludeSelfRegisteredUsersFilter(),
                  excludeInactiveUsersFilter(),
                ),
              )
            : query(
                collectionRef,
                orderBy(searchField),
                where(searchField, '>=', lowered),
                where(searchField, '<=', lowered + PREFIX_END),
                excludeSelfRegisteredUsersFilter(),
                excludeInactiveUsersFilter(),
              );
        const snapshot = await getDocs(queryRef);
        setServerResults(snapshot.docs.map(userFromDoc));
      } catch {
        toast.error('Error en la búsqueda', { theme: 'colored' });
        setServerResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!term.trim()) {
      setServerResults([]);
      return;
    }
    const timeoutId = setTimeout(() => runSearch(field, term.trim()), 400);
    return () => clearTimeout(timeoutId);
  }, [term, field, runSearch]);

  const isSearchActive = term.trim().length > 0;

  // Sin búsqueda se listan solo quienes aún no son miembros. Al buscar también
  // aparecen los miembros, marcados, para que se vea que ya están dentro. Se
  // une la búsqueda del servidor con un filtro sobre lo ya cargado, que
  // encuentra también a usuarios cuyo texto está guardado con mayúsculas.
  const displayed = useMemo(
    () =>
      isSearchActive
        ? mergeById(serverResults, filterUsers(browse, field, term))
        : browse.filter((user) => !memberIds.has(user.id)),
    [isSearchActive, serverResults, browse, field, term, memberIds],
  );

  const selectable = displayed.filter((user) => !memberIds.has(user.id));
  const selectedUsers = Object.values(selected);
  const allVisibleSelected =
    selectable.length > 0 && selectable.every((user) => selected[user.id]);

  const toggleUser = (user: User) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[user.id]) delete next[user.id];
      else next[user.id] = user;
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = { ...prev };
      selectable.forEach((user) => {
        if (allVisibleSelected) delete next[user.id];
        else next[user.id] = user;
      });
      return next;
    });

  const handleAdd = async () => {
    const { allowed, rejected } = planAddition(selectedUsers, audienceId);
    if (allowed.length === 0) {
      toast.error(
        'Ninguno de los seleccionados puede unirse: ya están en el máximo de audiencias.',
        { theme: 'colored' },
      );
      return;
    }

    setIsSaving(true);
    try {
      await addMembers(
        allowed.map((user) => user.id),
        audienceId,
      );
      onAdded(allowed);
      toast.success(
        `${allowed.length} ${plural(allowed.length, 'miembro agregado', 'miembros agregados')}`,
        { theme: 'colored' },
      );
      if (rejected.length > 0) {
        toast.warn(
          `${rejected.length} ${plural(rejected.length, 'usuario no se agregó', 'usuarios no se agregaron')}: ya están en el máximo de audiencias.`,
          { theme: 'colored' },
        );
      }
      setIsSaving(false);
      onClose();
    } catch {
      toast.error('Error al agregar los miembros', { theme: 'colored' });
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 px-4 pb-3">
        <p className="text-sm text-gray-600">
          Busca y marca a quienes quieras sumar a esta audiencia.
        </p>
        <div className="flex gap-2">
          <select
            value={field}
            onChange={(e) => setField(e.target.value as MemberSearchField)}
            aria-label="Buscar por"
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {searchOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar usuario..."
            aria-label="Buscar usuario"
            autoFocus
            className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {isLoading || isSearching ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : displayed.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-gray-600">
          {isSearchActive
            ? 'No se encontraron usuarios con esa búsqueda.'
            : 'No hay más usuarios para agregar.'}
        </p>
      ) : (
        <>
          <label className="flex cursor-pointer items-center gap-3 border-y border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleAllVisible}
              disabled={selectable.length === 0}
              className="h-4 w-4"
              style={{ accentColor: '#d80027' }}
            />
            Seleccionar todos los que se ven ({selectable.length})
          </label>
          <ul className="divide-y divide-gray-100">
            {displayed.map((user) => {
              const isMember = memberIds.has(user.id);
              const isChecked = isMember || Boolean(selected[user.id]);
              return (
                <li key={user.id}>
                  <label
                    className={`flex items-start gap-3 px-4 py-3 ${
                      isMember
                        ? 'cursor-not-allowed bg-gray-50'
                        : 'cursor-pointer hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isMember}
                      onChange={() => toggleUser(user)}
                      className="mt-1 h-4 w-4"
                      style={{ accentColor: '#d80027' }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900">
                        {user.name} {user.lastname}
                        {isMember && (
                          <span className="ml-2 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                            Ya es miembro
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-gray-600">
                        {user.document} · {user.email}
                        {user.area ? ` · ${user.area}` : ''}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className="sticky bottom-0 mt-auto flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3">
        <span className="text-sm text-gray-700" aria-live="polite">
          {selectedUsers.length}{' '}
          {plural(selectedUsers.length, 'seleccionado', 'seleccionados')}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-12 rounded border border-gray-300 bg-white px-5 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            Cancelar
          </button>
          <Button
            label={
              selectedUsers.length > 0
                ? `Agregar ${selectedUsers.length}`
                : 'Agregar'
            }
            onClick={handleAdd}
            isLoading={isSaving}
            disabled={selectedUsers.length === 0}
            className="px-6"
          />
        </div>
      </div>
    </div>
  );
};

export default AddMembersPanel;
